/**
 * Velocity Settings Card
 *
 * Configure velocity calculation parameters for stockout predictions.
 * Clean, minimal design with user-centric settings that provide real value.
 *
 * Settings:
 * 1. Lookback period - Days of history for velocity calculation
 * 2. Minimum data required - Days of history needed before generating predictions
 *
 * Design Compliance:
 * - Polaris spacing scale (gap="400", gap="500")
 * - Collapsible card pattern matching other settings
 * - Subtext below header matching other sections
 * - Icon + Title header with chevron
 * - RangeSlider for intuitive numeric input
 */

import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  RangeSlider,
  Box,
  Icon,
  Collapsible,
} from '@shopify/polaris';
import {
  ChartVerticalIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@shopify/polaris-icons';
import { useCallback } from 'react';

export type LookbackDays = 7 | 14 | 30 | 60 | 90;
export type MinimumDataDays = 7 | 14 | 21;

export interface VelocitySettingsCardProps {
  /** Lookback period in days */
  velocityLookbackDays: LookbackDays;
  /** Minimum days of data required */
  minimumDataDays: MinimumDataDays;
  /** Handlers */
  onLookbackDaysChange: (value: LookbackDays) => void;
  onMinimumDataDaysChange: (value: MinimumDataDays) => void;
  /** Collapsible behavior */
  isCollapsible?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
}

export function VelocitySettingsCard({
  velocityLookbackDays,
  minimumDataDays,
  onLookbackDaysChange,
  onMinimumDataDaysChange,
  isCollapsible = false,
  isOpen = true,
  onToggle,
}: VelocitySettingsCardProps) {
  const handleLookbackChange = useCallback(
    (value: number) => {
      onLookbackDaysChange(value as LookbackDays);
    },
    [onLookbackDaysChange]
  );

  const handleMinimumDataChange = useCallback(
    (value: number) => {
      onMinimumDataDaysChange(value as MinimumDataDays);
    },
    [onMinimumDataDaysChange]
  );

  const content = (
    <Box paddingBlockStart="200">
      <BlockStack gap="500">
        {/* Lookback Period */}
        <BlockStack gap="200">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="p" variant="bodyMd">Lookback period</Text>
            <div
              style={{
                padding: '4px 12px',
                borderRadius: '8px',
                backgroundColor: 'var(--p-color-bg-surface-secondary)',
                color: 'var(--p-color-text)',
                border: '1px solid var(--p-color-border)',
                fontWeight: 600,
                fontSize: '13px',
              }}
            >
              {velocityLookbackDays} days
            </div>
          </InlineStack>
          <RangeSlider
            label="Lookback period"
            labelHidden
            value={velocityLookbackDays}
            min={7}
            max={90}
            step={1}
            onChange={handleLookbackChange}
            output
          />
          <Text as="p" variant="bodySm" tone="subdued">
            Days of sales history used to calculate velocity
          </Text>
        </BlockStack>

        {/* Minimum Data Required */}
        <BlockStack gap="200">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="p" variant="bodyMd">Minimum data required</Text>
            <div
              style={{
                padding: '4px 12px',
                borderRadius: '8px',
                backgroundColor: 'var(--p-color-bg-surface-secondary)',
                color: 'var(--p-color-text)',
                border: '1px solid var(--p-color-border)',
                fontWeight: 600,
                fontSize: '13px',
              }}
            >
              {minimumDataDays} days
            </div>
          </InlineStack>
          <RangeSlider
            label="Minimum data required"
            labelHidden
            value={minimumDataDays}
            min={7}
            max={21}
            step={1}
            onChange={handleMinimumDataChange}
            output
          />
          <Text as="p" variant="bodySm" tone="subdued">
            Days of sales history needed before generating predictions
          </Text>
        </BlockStack>
      </BlockStack>
    </Box>
  );

  const header = (
    <InlineStack align="space-between" blockAlign="start">
      <InlineStack gap="200" blockAlign="start">
        <Box paddingBlockStart="050">
          <Icon source={ChartVerticalIcon} tone="subdued" />
        </Box>
        <BlockStack gap="100">
          <Text as="h2" variant="headingMd">
            Velocity calculation
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            Configure how sales velocity is calculated for stockout predictions.
          </Text>
        </BlockStack>
      </InlineStack>
      {isCollapsible && (
        <Box paddingBlockStart="050">
          <Icon source={isOpen ? ChevronUpIcon : ChevronDownIcon} tone="subdued" />
        </Box>
      )}
    </InlineStack>
  );

  return (
    <Card>
      <BlockStack gap="400">
        {isCollapsible ? (
          <button
            onClick={onToggle}
            aria-expanded={isOpen}
            aria-controls="section-velocity-settings"
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            {header}
          </button>
        ) : (
          header
        )}

        {isCollapsible ? (
          <Collapsible
            open={isOpen}
            id="section-velocity-settings"
            transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
          >
            {content}
          </Collapsible>
        ) : (
          content
        )}
      </BlockStack>
    </Card>
  );
}
